import { logInfo, logError, logWarn } from '../../utilities/pinpad-v2/logger'

class SerialPortService {
  constructor() {
    this.port = null
    this.isGranted = false

    if ('serial' in navigator && typeof navigator.serial?.addEventListener === 'function') {
      navigator.serial.addEventListener('disconnect', (event) => {
        if (event?.target === this.port) {
          this.port = null
          this.isGranted = false
        }
      })
    }
  }

  terminalVendors = [
    { usbVendorId: 2816, name: 'Ingenico' },
    { usbVendorId: 4554, name: 'Verifone' },
    { usbVendorId: 1478, name: 'WiseEasy' },
    { usbVendorId: 12995, name: 'Newland' },
    { usbVendorId: 12216, name: 'PAX' }
  ]

  async requestPort() {
    if (!('serial' in navigator)) {
      const error = new Error('Web Serial API not supported in this browser')
      error.code = 'SERIAL_NOT_SUPPORTED'
      throw error
    }

    try {
      const previouslyGrantedPorts = await navigator.serial.getPorts()

      if (previouslyGrantedPorts.length > 0) {
        this.port = previouslyGrantedPorts[0]
        this.isGranted = true

        const info = this.port.getInfo()
        logInfo('Serial port granted', {
          vendorId: info?.usbVendorId,
          productId: info?.usbProductId
        })

        return info
      }

      const filters = this.terminalVendors.map((vendor) => ({ usbVendorId: vendor.usbVendorId }))
      this.port = await navigator.serial.requestPort({ filters })
      this.isGranted = true

      const info = this.port.getInfo()
      logInfo('Serial port granted', {
        vendorId: info?.usbVendorId,
        productId: info?.usbProductId
      })

      return info
    } catch (error) {
      this.isGranted = false
      if (error?.name === 'NotFoundError') {
        logWarn('Serial port selection was canceled by user')
      } else {
        logError('Failed to request serial port', error)
      }
      throw error
    }
  }

  async ensureConnection() {
    if (this.port) {
      return true
    }

    if (!('serial' in navigator)) {
      this.isGranted = false
      return false
    }

    const ports = await navigator.serial.getPorts()

    if (ports.length > 0) {
      this.port = ports[0]
      this.isGranted = true
      return true
    }

    this.isGranted = false
    return false
  }

  async getPreviouslyGrantedPorts() {
    if (!('serial' in navigator)) return []

    try {
      return await navigator.serial.getPorts()
    } catch (error) {
      logError('Failed to get previously granted serial ports', error)
      return []
    }
  }

  async hasPreviouslyGrantedPort() {
    const ports = await this.getPreviouslyGrantedPorts()
    return ports.length > 0
  }

  hasPermission() {
    return this.isGranted && !!this.port
  }

  getPort() {
    return this.port
  }
}

const serialPortService = new SerialPortService()

export default serialPortService
